import { generatePath } from '@toggle/helpers';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  matchPath,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router';

import { SectionTitle } from '~/components/section-title/SectionTitle';
import { useCompanyTheme } from '~/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { appPaths } from '~/routes/app-paths';
import { queryKeys } from '~/routes/query-params';
import { ThemeLogoIcon } from '~/views/theme-overview/components/theme-logo-icon/ThemeLogoIcon';
import { TopRankIcon } from '~/widgets/knowledge-graph-widget/components/top-rank-icon/TopRankIcon';
import { KnowledgeGraphTabType } from '~/widgets/knowledge-graph-widget/KnowledgeGraphWidget';
import { useThemeDrawer } from '~/widgets/theme-widget/theme-drawer/useThemeDrawer';

import { getKnowledgeGraphSearchParams } from '../../utils/knowledge-graph-utils';
import { LoadingCell } from '../shared/loading-cell/LoadingCell';
import * as S from './ThemeKnowledgeGraph.styles';

export interface ThemeKnowledgeGraphProps {
  entityTag: string;
  isInDrawer?: boolean;
}

/**
 * @deprecated
 */
export const ThemeKnowledgeGraph = ({
  entityTag,
  isInDrawer = true,
}: ThemeKnowledgeGraphProps) => {
  const { t } = useTranslation(['widget']);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { openDrawer } = useThemeDrawer();

  const {
    data = [],
    isFetching,
    error,
  } = useCompanyTheme({
    entityTag,
    limit: 3,
  });

  if (error) {
    throw error;
  }

  const isAssetOverviewPage = matchPath(
    { path: appPaths.assetOverview, end: false, caseSensitive: false },
    location.pathname
  );

  const getTooltip = (context: string) => {
    if (isAssetOverviewPage) {
      return undefined;
    }

    /** i18next-extract-mark-context-next-line ["themeExposure"] */
    return t('widget:assetOverview:knowledgeGraphTooltipRevamp', {
      context: context,
    });
  };

  const knowledgeGraphSearchParams = getKnowledgeGraphSearchParams({
    entityTag,
    searchParams,
    subtab: KnowledgeGraphTabType.ThemeExposure,
  }).toString();
  const linkToKnowledgeGraph = `${appPaths.assetOverview}?${knowledgeGraphSearchParams}`;
  const onClickHandler = isInDrawer
    ? (themeId: string) => {
        const requestPath = generatePath({
          basePath: `${appPaths.themeOverview}/${themeId}`,
          params: {
            [queryKeys.activeSubTab]: 'overview',
          },
        });

        navigate(requestPath);
      }
    : openDrawer;

  if (!data.length) {
    return null;
  }

  return (
    <div>
      <SectionTitle
        title={t('widget:assetOverview.themes')}
        variant="small"
        linkTo={linkToKnowledgeGraph}
        tooltip={getTooltip('themeExposure')}
      />
      {isFetching ? (
        <div>
          {Array.from({ length: 3 }).map((_, idx) => (
            <S.ThemeRow key={idx}>
              <LoadingCell />
            </S.ThemeRow>
          ))}
        </div>
      ) : (
        <div>
          {data.map(d => (
            <S.ThemeRow
              onClick={() => onClickHandler(d.theme.id)}
              key={d.theme.name}
            >
              <S.ThemeNameWrapper>
                <ThemeLogoIcon
                  name={d.theme.name}
                  logo_url={d.theme.logo_url}
                  isCondensed={false}
                  fallbackIcon={
                    <S.LogoWrapper>{d.theme.name.charAt(0)}</S.LogoWrapper>
                  }
                />
                <S.ThemeName>{d.theme.name}</S.ThemeName>
              </S.ThemeNameWrapper>
              <TopRankIcon rank={d.relationship.rank} iconSize={28} />
            </S.ThemeRow>
          ))}
        </div>
      )}
    </div>
  );
};

ThemeKnowledgeGraph.Styled = S;
